// Voice-to-notes composer (Brian Ream questionnaire; Grant go 2026-06-13).
//
// Reusable across all six note surfaces (household, contact,
// service_record, opportunity, transaction, rhythm_instance — widened
// 2026-08-12 to match the typed composer, which already covered all six).
// Flow:
//   1. Record a voice memo (useAudioRecorder → MediaRecorder)
//   2. Stop → upload audio (base64) → Whisper transcript
//   3. Auto-structure the transcript into a clean titled note (Claude);
//      raw transcript stays available behind a toggle
//   4. Advisor edits title/body, saves → note created (source=
//      voice_transcribed) + AI-suggested follow-up tasks returned
//   5. Approve/dismiss each suggested task inline (reuses the existing
//      followup-suggestions pipeline → Microsoft To-Do via Flow A)
//
// Every AI-touched surface is labeled (sparkles) per CLAUDE.md: AI is
// quiet but never masquerades as human-authored.

import { useEffect, useRef, useState } from "react";
import { Mic, Square, Sparkles, Loader2, Check } from "lucide-react";

import {
  api,
  type HouseholdNoteKind,
  type VoiceNoteSuggestion,
  ApiError,
} from "../api";
import { useAudioRecorder, blobToBase64 } from "../hooks/useAudioRecorder";

type Phase =
  | "idle"
  | "recording"
  | "transcribing"
  | "review"
  | "saving"
  | "done";

// Hard caps so a long memo can't waste a big upload that the server
// rejects with a 413. Duration auto-stops the recording; size is the
// authoritative guard (mirrors the server's MAX_AUDIO_BYTES = 10 MB) for
// the rare high-bitrate browser that fills 10 MB before the time cap.
const MAX_RECORDING_SECONDS = 8 * 60; // 8 minutes
const MAX_RECORDING_BYTES = 10 * 1024 * 1024; // 10 MB (matches server cap)

export function VoiceNoteComposer({
  entityType,
  entityId,
  onSaved,
}: {
  entityType: HouseholdNoteKind;
  entityId: string;
  onSaved?: () => void;
}) {
  const recorder = useAudioRecorder();
  // Guards the gap between click and getUserMedia resolving so a
  // double-click can't spin up a second recorder (hot-mic leak).
  const startingRef = useRef(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [rawTranscript, setRawTranscript] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [aiCleaned, setAiCleaned] = useState(false);
  // Nate 2026-07-09 — hold the AI-cleaned body separately so the advisor
  // can switch freely between the original transcript, the cleaned
  // version, and a summary without losing any of them.
  const [cleanedBody, setCleanedBody] = useState("");
  const [summarizing, setSummarizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<VoiceNoteSuggestion[]>([]);
  const [resolved, setResolved] = useState<Record<string, "approved" | "dismissed">>(
    {},
  );

  // Stop the mic stream if the component unmounts mid-recording (advisor
  // navigates away / tab closes). Without this, getUserMedia tracks stay
  // live and the browser keeps the mic indicator on — a hot-mic leak.
  // recorder.reset() is a stable useCallback, so capturing it once is safe.
  useEffect(() => {
    return () => recorder.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-stop at the duration cap. The phase flips to "transcribing"
  // inside stopAndTranscribe, so this effect can't re-fire for the same
  // recording.
  useEffect(() => {
    if (phase === "recording" && recorder.seconds >= MAX_RECORDING_SECONDS) {
      void stopAndTranscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, recorder.seconds]);

  async function startRecording() {
    if (startingRef.current || phase === "recording") return;
    startingRef.current = true;
    setError(null);
    try {
      const res = await recorder.start();
      if (!res.ok) {
        // Permission denied / unsupported — surface the reason, stay idle.
        setError(res.error);
        setPhase("idle");
        return;
      }
      setPhase("recording");
    } finally {
      startingRef.current = false;
    }
  }

  async function stopAndTranscribe() {
    setPhase("transcribing");
    const blob = await recorder.stop();
    if (!blob) {
      setError("No audio was captured. Try recording again.");
      setPhase("idle");
      recorder.reset();
      return;
    }
    if (blob.size > MAX_RECORDING_BYTES) {
      // Reject locally instead of base64-encoding ~13 MB and eating a
      // server 413.
      setError(
        "That recording is too long to transcribe. Please record a shorter note (under ~8 minutes).",
      );
      setPhase("idle");
      recorder.reset();
      return;
    }
    try {
      const audioBase64 = await blobToBase64(blob);
      const { transcript } = await api.transcribeVoiceNote(audioBase64);
      recorder.reset();
      if (!transcript.trim()) {
        setError("Couldn't hear anything in that recording. Try again.");
        setPhase("idle");
        return;
      }
      setRawTranscript(transcript);
      // Best-effort cleanup; falls back to the raw transcript.
      try {
        const structured = await api.structureVoiceNote(transcript);
        setTitle(structured.title ?? "");
        setBodyText(structured.body);
        setAiCleaned(structured.structured);
        // Only stash a cleaned version when the AI actually cleaned it
        // (structured:false means body === the raw transcript fallback).
        setCleanedBody(structured.structured ? structured.body : "");
      } catch {
        setTitle("");
        setBodyText(transcript);
        setAiCleaned(false);
        setCleanedBody("");
      }
      setPhase("review");
    } catch (err) {
      recorder.reset();
      setError(
        err instanceof ApiError
          ? err.message
          : "Transcription failed — try again shortly.",
      );
      setPhase("idle");
    }
  }

  async function save() {
    if (bodyText.trim().length === 0) {
      setError("The note is empty.");
      return;
    }
    setPhase("saving");
    setError(null);
    try {
      const { suggestions: sugg } = await api.createVoiceNote({
        entity_type: entityType,
        entity_id: entityId,
        title: title.trim() || null,
        body: bodyText.trim(),
      });
      setSuggestions(sugg);
      setPhase("done");
      onSaved?.();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't save the note.",
      );
      setPhase("review");
    }
  }

  // Nate 2026-07-09 — the Summarize button: re-run the AI on the original
  // transcript in "summary" mode and drop the short digest into the body.
  // Summarizes the raw transcript (the most faithful source) when we have
  // it, else the current body. Failures leave the note untouched.
  async function summarize() {
    const source = rawTranscript.trim() || bodyText.trim();
    if (source.length === 0 || summarizing) return;
    setSummarizing(true);
    setError(null);
    try {
      const res = await api.structureVoiceNote(source, { mode: "summary" });
      if (res.structured) {
        setBodyText(res.body);
        setAiCleaned(true);
        if (res.title) setTitle((t) => t || res.title!);
      } else {
        setError(
          "Summarize isn't available right now — your note is unchanged.",
        );
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't summarize just now.",
      );
    } finally {
      setSummarizing(false);
    }
  }

  function discard() {
    recorder.reset();
    setPhase("idle");
    setTitle("");
    setBodyText("");
    setRawTranscript("");
    setShowRaw(false);
    setAiCleaned(false);
    setCleanedBody("");
    setSummarizing(false);
    setError(null);
    setSuggestions([]);
    setResolved({});
  }

  async function approve(s: VoiceNoteSuggestion) {
    setResolved((r) => ({ ...r, [s.id]: "approved" }));
    try {
      await api.approveFollowupSuggestion(s.id);
    } catch {
      setResolved((r) => {
        const next = { ...r };
        delete next[s.id];
        return next;
      });
    }
  }
  async function dismiss(s: VoiceNoteSuggestion) {
    setResolved((r) => ({ ...r, [s.id]: "dismissed" }));
    try {
      await api.dismissFollowupSuggestion(s.id);
    } catch {
      setResolved((r) => {
        const next = { ...r };
        delete next[s.id];
        return next;
      });
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  if (phase === "done") {
    return (
      <div className="voice-note-composer is-done">
        <div className="voice-note-saved">
          <Check size={16} strokeWidth={1.5} aria-hidden /> Note saved.
        </div>
        {suggestions.length > 0 && (
          <div className="voice-note-suggestions">
            <div className="voice-note-suggestions-title">
              <Sparkles size={14} strokeWidth={1.5} aria-hidden /> Suggested
              follow-ups
            </div>
            <ul>
              {suggestions.map((s) => {
                const status = resolved[s.id];
                return (
                  <li key={s.id} className="voice-note-suggestion">
                    <span className="voice-note-suggestion-title">
                      {s.suggestion_title}
                      {s.suggested_due_date ? ` · due ${s.suggested_due_date}` : ""}
                    </span>
                    {status ? (
                      <span className="voice-note-suggestion-status">
                        {status === "approved" ? "Added to tasks" : "Dismissed"}
                      </span>
                    ) : (
                      <span className="voice-note-suggestion-actions">
                        <button
                          type="button"
                          className="dash-btn"
                          onClick={() => void approve(s)}
                        >
                          <Check size={14} strokeWidth={1.5} aria-hidden /> Add task
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => void dismiss(s)}
                        >
                          Dismiss
                        </button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <button type="button" className="dash-btn" onClick={discard}>
          <Mic size={14} strokeWidth={1.5} aria-hidden /> Record another
        </button>
      </div>
    );
  }

  if (phase === "review") {
    return (
      <div className="voice-note-composer is-review">
        {aiCleaned && (
          <div className="voice-note-ai-label">
            <Sparkles size={14} strokeWidth={1.5} aria-hidden /> Cleaned up by
            AI from your recording · review before saving
          </div>
        )}
        <input
          type="text"
          className="voice-note-title-input"
          placeholder="Note title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="voice-note-body-input"
          rows={8}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
        />
        {/* Nate 2026-07-09 — switch the body between the original
            transcript, the AI-cleaned version, and a short summary,
            without losing any of them. */}
        <div className="voice-note-source-actions">
          {rawTranscript && bodyText.trim() !== rawTranscript.trim() && (
            <button
              type="button"
              className="dash-btn"
              disabled={summarizing}
              onClick={() => {
                setBodyText(rawTranscript);
                setAiCleaned(false);
              }}
            >
              Use original transcript
            </button>
          )}
          {cleanedBody && bodyText.trim() !== cleanedBody.trim() && (
            <button
              type="button"
              className="dash-btn"
              disabled={summarizing}
              onClick={() => {
                setBodyText(cleanedBody);
                setAiCleaned(true);
              }}
            >
              Use AI-cleaned version
            </button>
          )}
          {(rawTranscript || bodyText.trim().length > 0) && (
            <button
              type="button"
              className="dash-btn"
              disabled={summarizing}
              onClick={() => void summarize()}
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden />{" "}
              {summarizing ? "Summarizing…" : "Summarize"}
            </button>
          )}
        </div>
        {rawTranscript && (
          <button
            type="button"
            className="link-button voice-note-raw-toggle"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? "Hide" : "Show"} original transcript
          </button>
        )}
        {showRaw && (
          <pre className="voice-note-raw">{rawTranscript}</pre>
        )}
        {error && <div className="voice-note-error">{error}</div>}
        <div className="voice-note-actions">
          <button type="button" className="dash-btn-primary" onClick={() => void save()}>
            Save note
          </button>
          <button type="button" className="link-button" onClick={discard}>
            Discard
          </button>
        </div>
      </div>
    );
  }

  if (phase === "transcribing" || phase === "saving") {
    return (
      <div className="voice-note-composer is-busy">
        <Loader2 size={16} className="spin" strokeWidth={1.5} aria-hidden />{" "}
        {phase === "transcribing"
          ? "Transcribing your recording…"
          : "Saving…"}
      </div>
    );
  }

  // idle / recording
  return (
    <div className="voice-note-composer">
      {phase === "recording" || recorder.state === "recording" ? (
        <button
          type="button"
          className="voice-note-record is-recording"
          onClick={() => void stopAndTranscribe()}
        >
          <Square size={16} strokeWidth={1.5} aria-hidden /> Stop ·{" "}
          {formatSeconds(recorder.seconds)}
        </button>
      ) : (
        <button
          type="button"
          className="voice-note-record"
          onClick={() => void startRecording()}
        >
          <Mic size={16} strokeWidth={1.5} aria-hidden /> Record a voice note
        </button>
      )}
      <span className="voice-note-hint">
        Speak your meeting notes — we'll transcribe and organize them.
      </span>
      {error && <div className="voice-note-error">{error}</div>}
    </div>
  );
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}
